import { Grid, makeStyles, Typography } from '@material-ui/core'
import { isEmpty } from 'lodash'
import { FC } from 'react'
import { Form } from 'react-final-form'
import {
    VictoryBar,
    VictoryChart,
    VictoryGroup,
    VictoryAxis,
    VictoryLabel,
    VictoryTooltip,
    VictoryLine,
    VictoryVoronoiContainer,
} from 'victory'
import MySelectCtrl from '../../../control/select/select'
import useTranslate from '../../../i18n/useTranslate'
import { ResponseStatus } from '../../../services/um/useDashboard'
import EmptyData from '../officer/EmptyData'
import Tooltip from '../../../control/tooltip/tooltip'

interface AverageStatusTimeProps {
    data: ResponseStatus[]
    onChange: (val: string) => void
}

export enum UserGroupIDType {
    researcher = '1',
    officer = '2',
    chairman = '3',
    committee = '4',
}

const AverageStatusTime: FC<AverageStatusTimeProps> = ({ data, onChange }) => {
    const classes = useStyles()
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'

    const dataLegend = data.map((item) => {
        return {
            id: item.id,
            name: item.label[lang],
            fill: item.color[0],
        }
    })

    let dataChart = data.map((item) => {
        return item.value.map((val: any) => {
            return { x: val.label[lang], y: val.value }
        })
    })
    if (data[0]?.value.length === 1) {
        dataChart = [
            [{ x: '..', y: 0 }, ...dataChart[0], { x: '.', y: 0 }],
            [{ x: '..', y: 0 }, ...dataChart[1], { x: '.', y: 0 }],
            [{ x: '..', y: 0 }, ...dataChart[2], { x: '.', y: 0 }],
        ]
    }

    const standardLine = data.map((item, index) => {
        let x = 0
        if (item.value.length > 1) {
            if (index === 1) {
                if (data[1].value.length === 2) {
                    x += 0.14
                } else {
                    x += 0.16
                }
            } else if (index === 2) {
                if (data[1].value.length < 3) {
                    x += 0.143
                } else {
                    x += 0.165
                }
            }
        } else {
            if (index === 0) {
                x += 0.9
            } else if (index === 1) {
                x += 0.05 + index
            }
        }
        return item.value.map((val: any, idx) => {
            if (idx === 0) {
                x += 0.9
            } else {
                x += 0
            }
            return { x: idx + x, y: val?.standardLine }
        })
    })

    const userGroupID = [
        {
            value: UserGroupIDType.researcher,
            label: translate.languageStore.currentLanguage === 'th' ? 'นักวิจัย' : 'Researcher',
        },
        {
            value: UserGroupIDType.officer,
            label: translate.languageStore.currentLanguage === 'th' ? 'เจ้าหน้าที่' : 'Officer',
        },
        {
            value: UserGroupIDType.chairman,
            label: translate.languageStore.currentLanguage === 'th' ? 'ประธาน/รองประธาน/เลขาธิการ' : 'Chairman',
        },
        {
            value: UserGroupIDType.committee,
            label: translate.languageStore.currentLanguage === 'th' ? 'กรรมการ' : 'Committee',
        },
    ]

    const barColor = data.map((item) => {
        return (
            <svg viewBox="0 0 0  0" height={0} width={0}>
                <defs>
                    <linearGradient id={`myGradient_${item.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={item.color[0]} />
                        <stop offset="100%" stopColor={item.color[0]} />
                    </linearGradient>
                </defs>
            </svg>
        )
    })
    console.log(standardLine)
    return (
        <>
            <Grid container justify="space-between">
                <Typography className={classes.header}>{translate.get('UM.DASHBOARD.AVERAGE_STATUS')}</Typography>

                <Grid item className={classes.selectUser}>
                    <Form onSubmit={() => {}}>
                        {() => (
                            <MySelectCtrl
                                name="userGroup"
                                option={userGroupID}
                                defaultValue={['2']}
                                onChangeSelect={onChange}
                            />
                        )}
                    </Form>
                </Grid>
            </Grid>

            <div className={classes.chart}>
                <EmptyData isEmpty={isEmpty(data)}>
                    <VictoryChart
                        domainPadding={{ x: 80, y: 10 }}
                        padding={{ bottom: 16, left: 32, right: 32, top: 32 }}
                        width={700}
                        height={250}
                        containerComponent={<VictoryVoronoiContainer voronoiBlacklist={['bar1', 'bar2', 'bar3']} />}
                    >
                        <VictoryAxis
                            label={translate.get('UM.DASHBOARD.ACTIVITIES')}
                            axisLabelComponent={
                                <VictoryLabel
                                    dy={-30}
                                    dx={335}
                                    style={{ fontSize: '8px', fontFamily: 'Prompt', fill: '#B9B9B9' }}
                                />
                            }
                            style={{
                                axis: { stroke: '#D9D9D9' },
                                tickLabels: { fontSize: '8px', padding: 5, fill: '#3B4859', fontFamily: 'Prompt' },
                            }}
                        />
                        <VictoryAxis
                            dependentAxis
                            label={translate.get('UM.DASHBOARD.UNIT_DAY')}
                            axisLabelComponent={
                                <VictoryLabel
                                    dy={-105}
                                    dx={25}
                                    angle={0}
                                    style={{ fontSize: '7px', fontFamily: 'Prompt', fill: '#B9B9B9' }}
                                />
                            }
                            style={{
                                axis: { stroke: '#D9D9D9' },
                                grid: { stroke: '#818e99', strokeWidth: 0.2 },
                                tickLabels: { fontSize: '8px', padding: 5, fill: '#3B4859', fontFamily: 'Prompt' },
                            }}
                        />
                        <VictoryGroup
                            offset={30}
                            style={{ data: { width: 20 } }}
                            colorScale={['url(#myGradient_1)', 'url(#myGradient_2)', 'url(#myGradient_3)']}
                        >
                            <VictoryBar
                                labels={() => ''}
                                data={dataChart[0]}
                                labelComponent={
                                    <VictoryTooltip
                                        flyoutComponent={
                                            <CustomLabel label={data?.[0]?.label[lang]} renderInPortal={false} />
                                        }
                                    />
                                }
                                name="bar1"
                            />
                            <VictoryBar
                                labels={() => ''}
                                data={dataChart[1]}
                                labelComponent={
                                    <VictoryTooltip
                                        flyoutComponent={
                                            <CustomLabel label={data?.[1]?.label[lang]} renderInPortal={false} />
                                        }
                                    />
                                }
                                name="bar2"
                            />
                            <VictoryBar
                                labels={() => ''}
                                data={dataChart[2]}
                                labelComponent={
                                    <VictoryTooltip
                                        flyoutComponent={
                                            <CustomLabel label={data?.[2]?.label[lang]} renderInPortal={false} />
                                        }
                                    />
                                }
                                name="bar3"
                            />
                        </VictoryGroup>
                        {standardLine[0]?.map((item, idx) => {
                            const dataLine = [
                                {
                                    ...item,
                                    x:
                                        standardLine[0].length === 2
                                            ? 0.97 + idx
                                            : standardLine[0].length === 1
                                            ? 1.945
                                            : 0.822 + idx,
                                },
                                item,
                            ]
                            if (standardLine[0].length === 2) {
                                dataLine.push({ ...item, x: 0.9 + idx })
                            } else if (standardLine[0].length === 3) {
                                dataLine.push({ ...item, x: 0.032 + item.x })
                            } else if (standardLine[0].length > 3) {
                                dataLine.push({ ...item, x: 0.6 + idx })
                            }
                            return (
                                <VictoryLine
                                    key={idx}
                                    style={{ data: { stroke: '#c43a31' } }}
                                    data={dataLine}
                                    labels={() => ''}
                                    labelComponent={
                                        data[0]?.value.length > 3 && idx === 2 ? (
                                            <></>
                                        ) : (
                                            <VictoryTooltip
                                                flyoutComponent={
                                                    <CustomLabelLine
                                                        label={data?.[0]?.label[lang]}
                                                        renderInPortal={false}
                                                        dataBar={
                                                            data[0]?.value.length === 1
                                                                ? dataChart[0][idx + 1]
                                                                : dataChart[0][idx]
                                                        }
                                                    />
                                                }
                                            />
                                        )
                                    }
                                />
                            )
                        })}
                        {standardLine[1]?.map((item, idx) => {
                            let dataLine = [
                                {
                                    ...item,
                                    x:
                                        standardLine[0].length === 2
                                            ? 0.973 + idx
                                            : standardLine[0].length === 1
                                            ? 2.1
                                            : 0.941 + idx,
                                },
                                item,
                            ]
                            if (standardLine[1].length === 2) {
                                dataLine.push({ ...item, x: 0.974 + idx })
                            } else if (standardLine[1].length === 3) {
                                dataLine.push({ ...item, x: item.x })
                            } else if (standardLine[1].length > 3) {
                                dataLine.push({ ...item, x: 0.91 + idx })
                                dataLine.push({ ...item, x: 1.19 + idx })
                            }
                            return (
                                <VictoryLine
                                    key={idx}
                                    style={{ data: { stroke: '#c43a31' } }}
                                    data={dataLine}
                                    labels={() => ''}
                                    labelComponent={
                                        <VictoryTooltip
                                            flyoutComponent={
                                                <CustomLabelLine
                                                    label={data?.[1]?.label[lang]}
                                                    renderInPortal={false}
                                                    dataBar={
                                                        data[1]?.value.length === 1
                                                            ? dataChart[1][idx + 1]
                                                            : dataChart[1][idx]
                                                    }
                                                />
                                            }
                                        />
                                    }
                                />
                            )
                        })}
                        {standardLine[2]?.map((item, idx) => {
                            let dataLine =
                                standardLine[0].length === 1
                                    ? [
                                          {
                                              ...item,
                                              x: standardLine[0].length === 1 ? 2.105 : 0,
                                          },
                                      ]
                                    : []
                            if (standardLine[2].length === 1) {
                                dataLine.push({ ...item, x: 2.25 })
                            } else if (standardLine[2].length === 2) {
                                dataLine.push(item)
                                dataLine.push({ ...item, x: 1.11 + idx })
                            } else if (standardLine[2].length === 3) {
                                dataLine.push(item)
                                dataLine.push({ ...item, x: 1.1 + idx })
                                dataLine.push({ ...item, x: 0.116 + item.x })
                            } else if (standardLine[2].length > 3) {
                                dataLine.push({ ...item, x: 1.2 + idx })
                                dataLine.push({ ...item, x: 1.48 + idx })
                            }
                            return (
                                <VictoryLine
                                    key={idx}
                                    style={{ data: { stroke: '#c43a31' } }}
                                    width={0.5}
                                    data={dataLine}
                                    labels={() => ''}
                                    labelComponent={
                                        <VictoryTooltip
                                            flyoutComponent={
                                                <CustomLabelLine
                                                    label={data?.[2]?.label[lang]}
                                                    renderInPortal={false}
                                                    dataBar={
                                                        data[2]?.value.length === 1
                                                            ? dataChart[2][idx + 1]
                                                            : dataChart[2][idx]
                                                    }
                                                />
                                            }
                                        />
                                    }
                                />
                            )
                        })}
                    </VictoryChart>
                    {barColor}
                </EmptyData>
            </div>
            <div className={classes.legend}>
                {dataLegend.map((item) => {
                    return (
                        <div
                            style={{
                                display: 'inline-flex',
                                margin: '0px 8px',
                            }}
                            key={item.id}
                        >
                            <svg
                                height="16"
                                width="16"
                                style={{ margin: 'auto 8px', minHeight: '24px', minWidth: '24px' }}
                            >
                                <circle cx="50%" cy="50%" r="8" stroke="none" fill={item.fill} />
                            </svg>
                            <p style={{ margin: 'auto 0' }}>{item.name}</p>
                        </div>
                    )
                })}
            </div>
        </>
    )
}

const CustomLabel = (props: any) => {
    const { x, y, datum, label } = props
    const translate = useTranslate()
    const classes = useStyles()
    return (
        <g style={{ pointerEvents: 'none' }}>
            <foreignObject x={x + 20} y={y} height="50" width="150" style={{ border: 'none', display: 'inline-block' }}>
                <div className={classes.container}>
                    <Typography className={classes.tooltipLabel}>{label}</Typography>
                    <Typography className={classes.tooltipLabel}>{`${datum.xName} ${datum.y} ${translate.get(
                        'UM.DASHBOARD.UNIT_DAY',
                    )}`}</Typography>
                </div>
            </foreignObject>
        </g>
    )
}

const CustomLabelLine = (props: any) => {
    let { x, y, datum, dataBar } = props
    const translate = useTranslate()
    const classes = useStyles()
    if (datum.y === dataBar.y) {
        y -= 50
    } else if (datum.y + 1 === dataBar) {
        x -= 90
    }
    return (
        <g style={{ pointerEvents: 'none' }}>
            <foreignObject x={x + 10} y={y} height="50" width="150" style={{ border: 'none', display: 'inline-block' }}>
                <div className={classes.container}>
                    <Typography className={classes.tooltipLabel}>
                        {translate.get('UM.DASHBOARD.STANDARD_LINE')}
                    </Typography>
                    <Typography className={classes.tooltipLabel}>
                        {dataBar.x} {datum.y} {translate.get('UM.DASHBOARD.UNIT_DAY')}
                    </Typography>
                </div>
            </foreignObject>
        </g>
    )
}

const useStyles = makeStyles((theme) => ({
    chart: {
        display: 'block',

        padding: 0,
        margin: 0,
    },
    legend: {
        width: '100%',
        textAlign: 'center',
    },
    tooltipContainer: {
        position: 'relative',
        textAlign: 'center',
    },
    container: {
        margin: '0px',
        display: 'inline-block',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        boxShadow: `0px 4px 4px ${theme.palette.grey['50']}`,
        verticalAlign: 'middle',
        padding: '8px',
    },
    header: {
        margin: '0px 8px',
        fontWeight: 'bold',
    },
    tooltipLabel: {
        fontSize: '8px',
    },
    selectUser: {
        marginTop: '-45px',
    },
}))

export default AverageStatusTime
